export default class Quiz {
    constructor(container) {
        this.container = container
        this.introScreen = this.container.querySelector('.quiz-screen--intro')
        this.questionsScreen = this.container.querySelector('.quiz-screen--questions')
        this.questionScreens = Array.from(this.container.querySelectorAll('.quiz-question'))
        this.resultScreen = this.container.querySelector('.quiz-screen--results')
        this.resultItems = this.resultScreen
            ? Array.from(this.resultScreen.querySelectorAll('.quiz-result'))
            : []

        this.startButton = this.container.querySelector('.btn--quiz-start')
        this.answerButtons = Array.from(this.container.querySelectorAll('.btn--quiz-answer'))
        this.restartButtons = Array.from(this.container.querySelectorAll('.btn--quiz-restart'))
        this.backButton = this.container.querySelector('.btn--quiz-back')

        this.pagination = this.container.querySelector('.quiz-pagination')
        this.progressBar = this.container.querySelector('.progress-bar')
        this.currentPageNode = this.container.querySelector('.current-page')
        this.totalPagesNode = this.container.querySelector('.total-pages')
        this.a11yStatus = this.container.querySelector('.quiz-a11y-status')

        this.currentQuestionIndex = -1
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        this.transitionDuration = this.prefersReducedMotion ? 0 : 260
        this.isTransitioning = false
        this.transitionTimeout = null
        this.selectedResultIndex = null
        this.answersByQuestion = new Map()

        this.init()
    }

    init() {
        if (!this.introScreen || !this.resultScreen) return

        this.startHandler = this.handleStart.bind(this)
        this.answerHandler = this.handleAnswer.bind(this)
        this.restartHandler = this.handleRestart.bind(this)
        this.backHandler = this.handleBack.bind(this)

        if (this.startButton) {
            this.startButton.addEventListener('click', this.startHandler)
        }

        this.answerButtons.forEach((button) => {
            button.addEventListener('click', this.answerHandler)
        })

        this.restartButtons.forEach((button) => {
            button.addEventListener('click', this.restartHandler)
        })

        if (this.backButton) {
            this.backButton.addEventListener('click', this.backHandler)
        }

        if (this.progressBar) {
            this.progressBar.setAttribute('aria-valuemin', '0')
            this.progressBar.setAttribute('aria-valuemax', '100')
            this.progressBar.setAttribute('aria-valuenow', '0')
        }

        this.resetQuiz({ animate: false })
    }

    get totalQuestions() {
        return this.questionScreens.length
    }

    get isOnQuestionScreen() {
        return this.currentQuestionIndex >= 0 && this.currentQuestionIndex < this.totalQuestions
    }

    handleStart(event) {
        event.preventDefault()
        if (this.isTransitioning) return

        if (this.totalQuestions === 0) {
            this.showResult()
            return
        }

        this.currentQuestionIndex = 0
        this.render()
    }

    handleAnswer(event) {
        event.preventDefault()
        if (this.isTransitioning) return

        if (!this.isOnQuestionScreen) return

        this.storeAnswer(event.currentTarget)

        if (this.currentQuestionIndex < this.totalQuestions - 1) {
            this.currentQuestionIndex += 1
            this.render()
            return
        }

        this.showResult()
    }

    handleBack(event) {
        event.preventDefault()
        if (this.isTransitioning) return

        if (!this.isOnQuestionScreen) return

        if (this.currentQuestionIndex > 0) {
            this.currentQuestionIndex -= 1
            this.render()
            return
        }

        this.currentQuestionIndex = -1
        this.render()
    }

    handleRestart(event) {
        event.preventDefault()
        if (this.isTransitioning) return
        this.resetQuiz()
    }

    resetQuiz({ animate = true } = {}) {
        this.currentQuestionIndex = -1
        this.selectedResultIndex = null
        this.answersByQuestion.clear()
        this.setActiveQuestion(null)
        this.setActiveResult(null)
        this.render({ animate })
    }

    showResult() {
        this.selectedResultIndex = this.pickResultIndex()
        this.setActiveResult(this.selectedResultIndex)
        this.currentQuestionIndex = this.totalQuestions
        this.render()
        this.handleResultScreen()
        this.announce('Quiz completato. Risultato visualizzato.')
    }

    getTargetScreen() {
        if (this.currentQuestionIndex === -1) {
            return this.introScreen
        }

        if (this.isOnQuestionScreen) {
            return this.questionsScreen
        }

        return this.resultScreen
    }

    handleResultScreen() {
        // Placeholder: qui andranno le regole future basate su answersByQuestion.
        if (this.selectedResultIndex === null) {
            this.selectedResultIndex = this.pickResultIndex()
        }
        this.setActiveResult(this.selectedResultIndex)
    }

    storeAnswer(button) {
        if (!button) return

        const currentScreen = this.questionScreens[this.currentQuestionIndex]
        const questionId =
            button.dataset.questionId ||
            currentScreen?.dataset.questionId ||
            `q-${this.currentQuestionIndex + 1}`

        this.answersByQuestion.set(questionId, {
            questionId,
            answerId: button.dataset.answerId || null,
            answerIndex: button.dataset.answerIndex || null,
            value: (button.textContent || '').trim(),
        })
    }

    pickResultIndex() {
        if (!this.resultItems.length) return null
        return Math.floor(Math.random() * this.resultItems.length)
    }

    setActiveResult(index) {
        if (!this.resultItems.length) return

        this.resultItems.forEach((item, itemIndex) => {
            const isActive = index !== null && itemIndex === index
            item.hidden = !isActive
            item.classList.toggle('active-result', isActive)
            item.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        })
    }

    setActiveQuestion(index) {
        if (!this.questionScreens.length) return

        this.questionScreens.forEach((item, itemIndex) => {
            const isActive = index !== null && itemIndex === index
            item.hidden = !isActive
            item.classList.toggle('active-question', isActive)
            item.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        })
    }

    announce(message) {
        if (!this.a11yStatus || !message) return
        this.a11yStatus.textContent = message
    }

    getScreenFocusTarget(screen) {
        if (!screen) return null

        if (screen === this.questionsScreen && this.isOnQuestionScreen) {
            const activeQuestion = this.questionScreens[this.currentQuestionIndex] || null
            if (activeQuestion) {
                return (
                    activeQuestion.querySelector('.question, .btn--quiz-answer') || activeQuestion
                )
            }
        }

        return (
            screen.querySelector('.question, .title, .btn--quiz-start, .btn--quiz-restart') ||
            screen
        )
    }

    focusScreen(screen) {
        const target = this.getScreenFocusTarget(screen)
        if (!target) return

        if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1')
        }

        target.focus({ preventScroll: true })
    }

    updatePagination() {
        const isQuestionVisible = this.isOnQuestionScreen

        if (this.pagination) {
            this.pagination.hidden = !isQuestionVisible
        }

        if (!isQuestionVisible) {
            if (this.progressBar) {
                this.progressBar.style.setProperty('--quiz-progress', '0%')
                this.progressBar.setAttribute('aria-valuenow', '0')
                this.progressBar.setAttribute('aria-valuetext', 'Quiz non avviato')
            }
            return
        }

        const page = this.currentQuestionIndex + 1
        const total = this.totalQuestions
        const progress = total > 0 ? (page / total) * 100 : 0

        if (this.currentPageNode) {
            this.currentPageNode.textContent = String(page)
        }

        if (this.totalPagesNode) {
            this.totalPagesNode.textContent = String(total)
        }

        if (this.progressBar) {
            this.progressBar.style.setProperty('--quiz-progress', `${progress}%`)
            this.progressBar.setAttribute('aria-valuenow', String(Math.round(progress)))
            this.progressBar.setAttribute('aria-valuetext', `Domanda ${page} di ${total}`)
        }

        if (this.backButton) {
            const disableBack = this.currentQuestionIndex <= 0
            this.backButton.disabled = disableBack
            this.backButton.setAttribute('aria-disabled', disableBack ? 'true' : 'false')
        }
    }

    render({ animate = true } = {}) {
        const screens = [this.introScreen, this.questionsScreen, this.resultScreen]
        const nextScreen = this.getTargetScreen()
        if (!nextScreen) return

        this.setActiveQuestion(this.isOnQuestionScreen ? this.currentQuestionIndex : null)

        const currentScreen = this.container.querySelector('.quiz-screen.active-screen:not([hidden])')

        if (this.transitionTimeout) {
            window.clearTimeout(this.transitionTimeout)
            this.transitionTimeout = null
        }

        screens.forEach((screen) => {
            if (!screen || screen === currentScreen || screen === nextScreen) return
            screen.classList.remove('active-screen', 'is-entering', 'is-leaving')
            screen.hidden = true
            screen.setAttribute('aria-hidden', 'true')
        })

        if (!animate || !currentScreen || currentScreen === nextScreen) {
            screens.forEach((screen) => {
                if (!screen) return
                screen.classList.remove('active-screen', 'is-entering', 'is-leaving')
                screen.hidden = true
                screen.setAttribute('aria-hidden', 'true')
            })

            nextScreen.hidden = false
            nextScreen.classList.add('active-screen')
            nextScreen.setAttribute('aria-hidden', 'false')
            this.updatePagination()
            this.focusScreen(nextScreen)
            return
        }

        this.isTransitioning = true
        currentScreen.classList.remove('active-screen', 'is-entering')
        currentScreen.classList.add('is-leaving')

        nextScreen.hidden = false
        nextScreen.classList.add('active-screen', 'is-entering')
        nextScreen.setAttribute('aria-hidden', 'false')

        this.updatePagination()

        this.transitionTimeout = window.setTimeout(() => {
            currentScreen.hidden = true
            currentScreen.setAttribute('aria-hidden', 'true')
            currentScreen.classList.remove('is-leaving')
            nextScreen.classList.remove('is-entering')
            this.isTransitioning = false
            this.transitionTimeout = null
            this.focusScreen(nextScreen)
        }, this.transitionDuration)
    }

    destroy() {
        if (this.startButton) {
            this.startButton.removeEventListener('click', this.startHandler)
        }

        this.answerButtons.forEach((button) => {
            button.removeEventListener('click', this.answerHandler)
        })

        this.restartButtons.forEach((button) => {
            button.removeEventListener('click', this.restartHandler)
        })

        if (this.backButton) {
            this.backButton.removeEventListener('click', this.backHandler)
        }

        if (this.transitionTimeout) {
            window.clearTimeout(this.transitionTimeout)
            this.transitionTimeout = null
        }
    }

    static init() {
        Quiz.items = Array.from(document.querySelectorAll('.section--quiz .quiz-container')).map(
            (container) => new Quiz(container),
        )
    }
}
